import { useState, useEffect, type ReactElement } from 'react'
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Select,
    FormControl,
    Chip,
    ListSubheader,
} from '@mui/material'
import {
    Dashboard as DashboardIcon,
    BarChart as BarChartIcon,
    Speed as SpeedIcon,
    FolderOpen as ProjectsIcon,
    SmartToy as AgentsIcon,
    Settings as SettingsIcon,
    Payment as BillingIcon,
    Menu as MenuIcon,
    TrendingUp as TrendingUpIcon,
    Public as PublicIcon,
    DevicesOther as DevicesIcon,
    VpnKey as VpnKeyIcon,
    Dns as CollectionDomainIcon,
    MenuBook as DocsIcon,
    AdminPanelSettings as AdminIcon,
    Science as LensesIcon,
    ListAlt as EventsIcon,
    ShoppingCart as ConversionsIcon,
    Flag as GoalsIcon,
    FilterAlt as FunnelsIcon,
    AccountTree as AttributionIcon,
    TrackChanges as IntentIcon,
    Replay as RetentionIcon,
    BugReport as QualityIcon,
    Groups as SubTenantsIcon,
    Webhook as DestinationsIcon,
    DataUsage as UsageIcon,
    Storefront as PortalsIcon,
    Verified as ProofIcon,
    PersonSearch as PrivacyRequestsIcon,
    CloudUpload as ExportsIcon,
    PeopleAlt as TeamIcon,
    History as AuditIcon,
    Handshake as PartnerAccessIcon,
    Lock as SecurityIcon,
} from '@mui/icons-material'
import {
    appPath,
    DASHBOARD_AUTH_PATHS,
    DASHBOARD_HOME,
    normalizeOrgRole,
} from '@yourownmetric/shared'

import { useAppSelector, useAppDispatch } from '@/app/hooks'
import { logout, setCredentials } from '@/modules/auth/authSlice'
import {
    clearProject,
    resolveSelectedProject,
    selectProject,
    setSubTenant,
} from '@/app/projectSlice'
import { useListProjectsQuery } from '@/apis/projectsApi'
import { useGetProjectConfigQuery, useListSubTenantsQuery } from '@/apis/v2Api'
import { useGetMeQuery, useGetOrgQuery, useSwitchTenantMutation } from '@/apis/teamApi'
import { ROLE_LABELS } from '@/apis/teamTypes'

import { OrgSuspendedNotice } from './OrgSuspendedNotice'

const DRAWER_WIDTH = 240

interface NavItem {
    label: string
    icon: ReactElement
    path: string
    /** Only org owners/admins see it. */
    managersOnly?: boolean
    /** Only orgs a partner created and someone claimed (Settings > Access). */
    partnerOnly?: boolean
}

const NAV_SECTIONS: Array<{ label: string; items: NavItem[] }> = [
    {
        label: 'Analytics',
        items: [
            { label: 'Overview', icon: <BarChartIcon />, path: appPath('overview') },
            { label: 'Realtime', icon: <SpeedIcon />, path: appPath('realtime') },
            { label: 'Sources', icon: <TrendingUpIcon />, path: appPath('sources') },
            { label: 'Geography', icon: <PublicIcon />, path: appPath('geo') },
            { label: 'Devices', icon: <DevicesIcon />, path: appPath('devices') },
            { label: 'Events', icon: <EventsIcon />, path: appPath('events') },
        ],
    },
    {
        label: 'Conversions',
        items: [
            { label: 'Ecommerce', icon: <ConversionsIcon />, path: appPath('conversions') },
            { label: 'Goals', icon: <GoalsIcon />, path: appPath('goals') },
            { label: 'Funnels', icon: <FunnelsIcon />, path: appPath('funnels') },
            { label: 'Attribution', icon: <AttributionIcon />, path: appPath('attribution') },
            { label: 'Intent', icon: <IntentIcon />, path: appPath('intent') },
            { label: 'Retention', icon: <RetentionIcon />, path: appPath('retention') },
            { label: 'Errors & Performance', icon: <QualityIcon />, path: appPath('quality') },
            { label: 'Proof', icon: <ProofIcon />, path: appPath('proof') },
            {
                label: 'Privacy requests',
                icon: <PrivacyRequestsIcon />,
                path: appPath('privacy-requests'),
            },
        ],
    },
    {
        label: 'Manage',
        items: [
            { label: 'Projects', icon: <ProjectsIcon />, path: appPath('projects') },
            { label: 'Sub-tenants', icon: <SubTenantsIcon />, path: appPath('sub-tenants') },
            { label: 'Client portals', icon: <PortalsIcon />, path: appPath('portals') },
            { label: 'Destinations', icon: <DestinationsIcon />, path: appPath('destinations') },
            { label: 'Exports', icon: <ExportsIcon />, path: appPath('exports') },
            { label: 'Usage', icon: <UsageIcon />, path: appPath('usage') },
            { label: 'Dashboards', icon: <DashboardIcon />, path: appPath('dashboards') },
            { label: 'Lenses', icon: <LensesIcon />, path: appPath('lenses') },
            { label: 'AI Agents', icon: <AgentsIcon />, path: appPath('agents') },
        ],
    },
    {
        label: 'Account',
        items: [
            { label: 'API Keys', icon: <VpnKeyIcon />, path: appPath('settings/api-keys') },
            { label: 'Team', icon: <TeamIcon />, path: appPath('settings/team') },
            { label: 'Security', icon: <SecurityIcon />, path: appPath('settings/security') },
            {
                label: 'Audit log',
                icon: <AuditIcon />,
                path: appPath('settings/audit-log'),
                managersOnly: true,
            },
            {
                label: 'Access',
                icon: <PartnerAccessIcon />,
                path: appPath('settings/access'),
                managersOnly: true,
                partnerOnly: true,
            },
            {
                label: 'Collection domain',
                icon: <CollectionDomainIcon />,
                path: appPath('settings/collection-domain'),
            },
            { label: 'Settings', icon: <SettingsIcon />, path: appPath('settings') },
            { label: 'Billing', icon: <BillingIcon />, path: appPath('billing') },
        ],
    },
    {
        label: 'Developer',
        items: [{ label: 'Integration Guide', icon: <DocsIcon />, path: appPath('docs') }],
    },
]

const ADMIN_NAV_SECTION: { label: string; items: NavItem[] } = {
    label: 'Platform Admin',
    items: [{ label: 'Admin Panel', icon: <AdminIcon />, path: appPath('admin') }],
}

export function DashboardLayout() {
    const [mobileOpen, setMobileOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const navigate = useNavigate()
    const location = useLocation()
    const user = useAppSelector(s => s.auth.user)
    const tenant = useAppSelector(s => s.auth.tenant)
    const selectedProjectId = useAppSelector(s => s.project.selectedId)
    const subTenant = useAppSelector(s => s.project.subTenant)
    const dispatch = useAppDispatch()
    const mfaRequired = useAppSelector(s => s.auth.mfaRequired)
    const orgSuspended = useAppSelector(s => s.auth.orgSuspended)
    const securityPath = appPath('settings/security')

    const { data: projects = [], isSuccess: projectsLoaded } = useListProjectsQuery()
    const projectValid = projects.some(p => p.id === selectedProjectId)
    const { data: projectConfig } = useGetProjectConfigQuery(selectedProjectId ?? '', {
        skip: !projectValid,
    })
    const subTenantsOn = !!projectConfig && projectConfig.config.subTenants.mode !== 'off'
    const { data: subTenants = [] } = useListSubTenantsQuery(
        { projectId: selectedProjectId ?? '' },
        { skip: !projectValid || !subTenantsOn },
    )

    const { data: org } = useGetOrgQuery()
    const { data: me } = useGetMeQuery()
    const [switchTenant] = useSwitchTenantMutation()
    const orgs = me?.tenants ?? []
    const canManage = !!org?.can_manage
    const navSections = (
        user?.isSuperAdmin ? [...NAV_SECTIONS, ADMIN_NAV_SECTION] : NAV_SECTIONS
    ).map(section => ({
        ...section,
        items: section.items.filter(
            i => (!i.managersOnly || canManage) && (!i.partnerOnly || !!org?.partner_managed),
        ),
    }))

    const handleSwitch = async (tenantId: string) => {
        setAnchorEl(null)
        if (tenantId === tenant?.id) {
            return
        }
        const res = await switchTenant(tenantId).unwrap()
        dispatch(setCredentials({ token: res.token, user: res.user, tenant: res.tenant }))
        navigate(DASHBOARD_HOME)
    }

    // Keep the selection inside THIS account's projects: a stale id (another account
    // in the same browser, a deleted project) falls back to the first project.
    useEffect(() => {
        if (!projectsLoaded) {
            return
        }
        const next = resolveSelectedProject(projects, selectedProjectId)
        if (next !== selectedProjectId) {
            dispatch(next ? selectProject(next) : clearProject())
        }
    }, [projects, projectsLoaded, selectedProjectId, dispatch])

    const handleLogout = () => {
        dispatch(logout())
        navigate(DASHBOARD_AUTH_PATHS.login)
    }

    const drawer = (
        <Box>
            <Toolbar>
                <Typography variant="h6" noWrap sx={{ fontWeight: 700, color: 'primary.main' }}>
                    YOM
                </Typography>
            </Toolbar>
            <Divider />
            {(org ?? tenant) && (
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                        {org?.name ?? tenant?.name}
                    </Typography>
                    {org && (
                        <Chip
                            label={
                                org.role === 'viewer' ? 'Viewer · read-only' : ROLE_LABELS[org.role]
                            }
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5, height: 20, fontSize: 11 }}
                            data-testid="org-role"
                        />
                    )}
                </Box>
            )}
            <Divider />
            {navSections.map(section => (
                <Box key={section.label}>
                    <Typography
                        variant="overline"
                        sx={{
                            px: 2,
                            pt: 2,
                            pb: 0.5,
                            display: 'block',
                            color: 'text.secondary',
                            fontSize: 10,
                        }}
                    >
                        {section.label}
                    </Typography>
                    <List dense disablePadding>
                        {section.items.map(item => (
                            <ListItemButton
                                key={item.path}
                                selected={location.pathname === item.path}
                                onClick={() => {
                                    navigate(item.path)
                                    setMobileOpen(false)
                                }}
                                sx={{
                                    borderRadius: 1,
                                    mx: 1,
                                    mb: 0.25,
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        '& .MuiListItemIcon-root': { color: 'white' },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{ fontSize: 13 }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            ))}
        </Box>
    )

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
                    ml: { sm: `${DRAWER_WIDTH}px` },
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: 'none',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Toolbar>
                    <IconButton
                        edge="start"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* Project Selector */}
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                            value={selectedProjectId ?? ''}
                            onChange={e => dispatch(selectProject(e.target.value))}
                            displayEmpty
                            sx={{ '& .MuiSelect-select': { py: 0.75, fontSize: 14 } }}
                        >
                            {projects.length === 0 && (
                                <MenuItem value="" disabled>
                                    No projects
                                </MenuItem>
                            )}
                            {projects.map(p => (
                                <MenuItem key={p.id} value={p.id}>
                                    {p.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Sub-tenant filter applied to every v2 report */}
                    {(subTenantsOn || subTenant !== null) && (
                        <FormControl size="small" sx={{ minWidth: 180, ml: 1 }}>
                            <Select
                                value={
                                    subTenant === null
                                        ? '__all'
                                        : subTenant === ''
                                          ? '__own'
                                          : subTenant
                                }
                                onChange={e => {
                                    const v = e.target.value
                                    dispatch(
                                        setSubTenant(v === '__all' ? null : v === '__own' ? '' : v),
                                    )
                                }}
                                sx={{ '& .MuiSelect-select': { py: 0.75, fontSize: 14 } }}
                            >
                                <MenuItem value="__all">All sub-tenants</MenuItem>
                                <MenuItem value="__own">Project itself</MenuItem>
                                {subTenant !== null &&
                                    subTenant !== '' &&
                                    !subTenants.some(st => st.external_id === subTenant) && (
                                        <MenuItem value={subTenant}>{subTenant}</MenuItem>
                                    )}
                                {subTenants.map(st => (
                                    <MenuItem key={st.external_id} value={st.external_id}>
                                        {st.name
                                            ? `${st.name} (${st.external_id})`
                                            : st.external_id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <Box sx={{ flex: 1 }} />
                    <IconButton onClick={e => setAnchorEl(e.currentTarget)}>
                        <Avatar
                            sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}
                        >
                            {user?.name?.charAt(0).toUpperCase() ?? '?'}
                        </Avatar>
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                    >
                        <MenuItem disabled>
                            <Typography variant="body2">{user?.email}</Typography>
                        </MenuItem>
                        <Divider />
                        {orgs.length > 1 && <ListSubheader>Switch organization</ListSubheader>}
                        {orgs.length > 1 &&
                            orgs.map(o => (
                                <MenuItem
                                    key={o.id}
                                    selected={o.id === tenant?.id}
                                    onClick={() => {
                                        void handleSwitch(o.id)
                                    }}
                                >
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {o.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ ml: 2 }}
                                    >
                                        {ROLE_LABELS[normalizeOrgRole(o.role)]}
                                    </Typography>
                                </MenuItem>
                            ))}
                        {orgs.length > 1 && <Divider />}
                        <MenuItem onClick={handleLogout}>Logout</MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Mobile drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
                }}
            >
                {drawer}
            </Drawer>

            {/* Desktop drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                    },
                }}
                open
            >
                {drawer}
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
                    mt: '64px',
                    bgcolor: 'background.default',
                    minHeight: 'calc(100vh - 64px)',
                }}
            >
                {orgSuspended ? (
                    <OrgSuspendedNotice />
                ) : mfaRequired && location.pathname !== securityPath ? (
                    <Navigate to={securityPath} replace />
                ) : (
                    <Outlet />
                )}
            </Box>
        </Box>
    )
}
